import {
  Chain,
  ChainID,
  ChainType,
  EBlockchainFeature,
  EBlockchainType,
  Timeframe,
  SUI_ARCHIVE_GRPC_PATH,
} from '@ankr.com/chains-list';

import { GroupedEndpoints } from 'modules/endpoints/types';
import { StatsTimeframe } from 'modules/stats/types';

export const ALLORA_MAINNET_TENDERMINT_NAME = 'Allora Tendermint';
export const ALLORA_TESTNET_TENDERMINT_NAME = 'Allora Testnet Tendermint';
export const ZERO_G_TESTNET_TENDERMINT_NAME = '0G Galileo Testnet Tendermint';
export const SECRET_TENDERMINT_NAME = 'Secret Network Tendermint';
export const SEI_TENDERMINT_NAME = 'Sei Tendermint';
export const SEI_TESTNET_TENDERMINT_NAME = 'Sei Testnet Tendermint';
export const KAVA_TENDERMINT_NAME = 'Kava Tendermint';
export const KAVA_TESTNET_TENDERMINT_NAME = 'Kava Testnet Tendermint';
export const TAC_TENDERMINT_NAME = 'TAC Tendermint';
export const TAC_SPB_TENDERMINT_NAME = 'TAC SPB Tendermint';
export const TON_NAME = 'TON';

export const TAC_EVM_PATH = 'tac' as ChainID;
export const TAC_TENDERMINT_JSON_RPC_PATH = 'tac_tendermint' as ChainID;
export const TAC_TENDERMINT_REST_PATH = 'tac_tendermint' as ChainID;
export const TAC_COSMOS_REST_PATH = 'tac_cosmos' as ChainID;
export const TAC_COSMOS_GRPC_PATH = 'tac_grpc-web' as ChainID;

export const TAC_TESTNET_SPB_EVM_PATH = 'tac_spb' as ChainID;
export const TAC_TESTNET_SPB_COSMOS_GRPC_PATH = 'tac_spb_grpc-web' as ChainID;
export const TAC_TESTNET_SPB_COSMOS_REST_PATH = 'tac_spb_cosmos' as ChainID;
export const TAC_TESTNET_SPB_TENDERMINT_REST_PATH =
  'tac_spb_tendermint' as ChainID;
export const TAC_TESTNET_SPB_TENDERMINT_RPC_PATH =
  'tac_spb_tendermint' as ChainID;

export const ZKVERIFY_MAINNET_PATH = 'zkverify_mainnet' as ChainID;
export const ZKVERIFY_VOLTA_TESTNET_PATH = 'zkverify_volta_testnet' as ChainID;
export const ZKVERIFY_VFLOW_PARACHAIN_MAINNET_PATH = 'vflow_mainnet' as ChainID;
export const ZKVERIFY_VFLOW_PARACHAIN_TESTNET_PATH =
  'vflow_volta_testnet' as ChainID;

export const STELLAR_HORIZON_PATH = 'stellar_horizon' as ChainID;
export const STELLAR_SOROBAN_PATH = 'stellar_soroban' as ChainID;
export const STELLAR_TESTNET_HORIZON_PATH =
  'stellar_testnet_horizon' as ChainID;
export const STELLAR_TESTNET_SOROBAN_PATH =
  'stellar_testnet_soroban' as ChainID;

export const POLKADOT_ASSET_HUB_PATH = 'polkadot_mainnet_asset_hub' as ChainID;

export type ChainIDLinkMap = Partial<Record<ChainID, ChainID>>;

// for statistics we use paths. this mapper is used to map chain ids that are different from paths
export const CHAIN_ID_TO_PATH_MAP: ChainIDLinkMap = {
  [ChainID.ABCHAIN]: 'abchain_mainnet' as ChainID,
  [ChainID.ABCHAIN_TESTNET]: 'abchain_testnet' as ChainID,

  [ChainID.ALLORA_MAINNET_REST]: 'allora_mainnet' as ChainID,
  [ChainID.ALLORA_MAINNET_RPC]: 'allora_mainnet' as ChainID,
  [ChainID.ALLORA_MAINNET_COSMOS_REST]: 'allora_cosmos_mainnet' as ChainID,
  [ChainID.ALLORA_MAINNET_GRPC]: 'allora_mainnet_grpc' as ChainID,

  [ChainID.ALLORA_TESTNET_REST]: ChainID.ALLORA_TESTNET,
  [ChainID.ALLORA_TESTNET_RPC]: ChainID.ALLORA_TESTNET,
  [ChainID.ALLORA_TESTNET_COSMOS_REST]: 'allora_cosmos_testnet' as ChainID,
  [ChainID.ALLORA_TESTNET_GRPC]: 'allora_testnet_grpc' as ChainID,

  [ChainID.ZERO_G]: '0g_mainnet_evm' as ChainID,
  [ChainID.ZERO_G_EVM]: '0g_mainnet_evm' as ChainID,
  [ChainID.ZERO_G_BEACONKIT_REST]: '0g_mainnet_beaconkit' as ChainID,
  [ChainID.ZERO_G_GALILEO_BEACONKIT_REST]:
    '0g_galileo_testnet_beaconkit' as ChainID,
  [ChainID.ZERO_G_GALILEO_EVM]: '0g_galileo_testnet_evm' as ChainID,
  [ChainID.ZERO_G_GALILEO_TENDERMINT_REST]:
    '0g_galileo_testnet_tendermint' as ChainID,
  [ChainID.ZERO_G_GALILEO_TENDERMINT_RPC]:
    '0g_galileo_testnet_tendermint' as ChainID,

  [ChainID.SECRET_REST]: ChainID.SECRET,
  [ChainID.SECRET_RPC]: ChainID.SECRET,
  [ChainID.SECRET_COSMOS_GRPC_WEB]: ChainID.SECRET_COSMOS,
  [ChainID.SECRET_COSMOS_REST]: ChainID.SECRET_COSMOS,

  [ChainID.ZETACHAIN_COSMOS_REST_ATHENS_TESTNET]:
    'zetachain_athens_testnet' as ChainID,
  [ChainID.ZETACHAIN_EVM_ATHENS_TESTNET]:
    'zetachain_evm_athens_testnet' as ChainID,
  [ChainID.ZETACHAIN_TENDERMINT_REST_ATHENS_TESTNET]:
    'zetachain_tendermint_athens_testnet' as ChainID,
  [ChainID.ZETACHAIN_TENDERMINT_RPC_ATHENS_TESTNET]:
    'zetachain_tendermint_athens_testnet' as ChainID,
  [ChainID.ZETACHAIN_ATHENS_TESTNET]: 'zetachain_athens_testnet' as ChainID,

  [ChainID.TENET]: 'tenet_evm' as ChainID,
  [ChainID.TENET_EVM]: 'tenet_evm' as ChainID,

  [ChainID.HORIZEN]: 'horizen_eon' as ChainID,
  [ChainID.HORIZEN_EVM]: 'horizen_eon' as ChainID,
  [ChainID.HORIZEN_TESTNET]: 'horizen_gobi_testnet' as ChainID,
  [ChainID.HORIZEN_TESTNET_EVM]: 'horizen_gobi_testnet' as ChainID,

  [ChainID.BERACHAIN]: 'berachain_testnet' as ChainID,
  [ChainID.BERACHAIN_GUARDED_TESTNET_EVM]: 'berachain_testnet' as ChainID,

  [ChainID.SEI_COSMOS_REST]: 'sei_cosmos' as ChainID,
  [ChainID.SEI_REST]: ChainID.SEI,
  [ChainID.SEI_RPC]: ChainID.SEI,
  [ChainID.SEI_EVM]: 'sei_evm' as ChainID,
  [ChainID.SEI_TENDERMINT_GRPC]: 'sei_grpc' as ChainID,

  [ChainID.KAVA]: 'kava_evm' as ChainID,
  [ChainID.KAVA_COSMOS_REST]: 'kava_api' as ChainID,
  [ChainID.KAVA_EVM]: 'kava_evm' as ChainID,
  [ChainID.KAVA_TENDERMINT_REST]: 'kava_rpc' as ChainID,
  [ChainID.KAVA_TENDERMINT_RPC]: 'kava_rpc' as ChainID,
  [ChainID.KAVA_TENDERMINT_GRPC]: 'kava_grpc' as ChainID,
  [ChainID.KAVA_COSMOS_REST_TESTNET]: 'kava_testnet_api' as ChainID,
  [ChainID.KAVA_TESTNET]: 'kava_testnet_evm' as ChainID,
  [ChainID.KAVA_EVM_TESTNET]: 'kava_testnet_evm' as ChainID,
  [ChainID.KAVA_TENDERMINT_REST_TESTNET]: 'kava_testnet_rpc' as ChainID,
  [ChainID.KAVA_TENDERMINT_RPC_TESTNET]: 'kava_testnet_rpc' as ChainID,

  [ChainID.KITE]: 'kite_mainnet' as ChainID,

  [ChainID.STELLAR]: STELLAR_HORIZON_PATH,
  [ChainID.STELLAR_HORIZON]: STELLAR_HORIZON_PATH,
  [ChainID.STELLAR_SOROBAN]: STELLAR_SOROBAN_PATH,
  [ChainID.STELLAR_TESTNET_HORIZON]: STELLAR_TESTNET_HORIZON_PATH,
  [ChainID.STELLAR_TESTNET_SOROBAN]: STELLAR_TESTNET_SOROBAN_PATH,

  [ChainID.SUI_GRPC]: 'sui_grpc' as ChainID,
  [ChainID.SUI_GRPC_ARCHIVE]: SUI_ARCHIVE_GRPC_PATH as ChainID,
  [ChainID.SUI_GRAPHQL]: 'sui_graphql' as ChainID,
  [ChainID.SUI_TESTNET_GRPC]: 'sui_testnet_grpc' as ChainID,

  [ChainID.TAC_EVM]: TAC_EVM_PATH,
  [ChainID.TAC_TENDERMINT_JSON_RPC]: TAC_TENDERMINT_JSON_RPC_PATH,
  [ChainID.TAC_TENDERMINT_GRPC]: 'tac_tendermint_grpc' as ChainID,
  [ChainID.TAC_TENDERMINT_REST]: TAC_TENDERMINT_REST_PATH,
  [ChainID.TAC_COSMOS_REST]: TAC_COSMOS_REST_PATH,
  [ChainID.TAC_COSMOS_GRPC_WEB]: TAC_COSMOS_GRPC_PATH,
  [ChainID.TAC_TESTNET_SPB]: TAC_TESTNET_SPB_EVM_PATH,
  [ChainID.TAC_TESTNET_SPB_GRPC]: 'tac_spb_tendermint_grpc' as ChainID,
  [ChainID.TAC_TESTNET_SPB_COSMOS_GRPC]: TAC_TESTNET_SPB_COSMOS_GRPC_PATH,
  [ChainID.TAC_TESTNET_SPB_COSMOS_REST]: TAC_TESTNET_SPB_COSMOS_REST_PATH,
  [ChainID.TAC_TESTNET_SPB_EVM]: TAC_TESTNET_SPB_EVM_PATH,
  [ChainID.TAC_TESTNET_SPB_TENDERMINT_REST]:
    TAC_TESTNET_SPB_TENDERMINT_REST_PATH,
  [ChainID.TAC_TESTNET_SPB_TENDERMINT_RPC]: TAC_TESTNET_SPB_TENDERMINT_RPC_PATH,

  [ChainID.TON]: 'ton_api_v2' as ChainID,
  [ChainID.TON_REST]: 'ton_api_v2' as ChainID,
  [ChainID.TON_RPC]: 'ton_api_v2' as ChainID,

  [ChainID.FLARE_COSTON_EVM]: ChainID.FLARE_COSTON,

  [ChainID.FLARE_COSTON2_EVM]: ChainID.FLARE_COSTON2,

  [ChainID.FLARE_SONGBIRD_EVM]: ChainID.FLARE_SONGBIRD,

  [ChainID.BTC_MAINNET]: ChainID.BTC,

  [ChainID.NERVOS_EVM]: ChainID.NERVOS,

  [ChainID.TRON_REST]: ChainID.TRON,

  [ChainID.FUEL_REST]: 'fuel' as ChainID,
  [ChainID.FUEL_SEPOLIA]: 'fuel_sepolia' as ChainID,

  [ChainID.STORY]: 'story_mainnet' as ChainID,
  [ChainID.STORY_ODYSSEY]: 'story_odyssey' as ChainID,
  [ChainID.STORY_AENEID]: 'story_aeneid_testnet' as ChainID,

  [ChainID.CORN_MAIZENET]: 'corn_maizenet' as ChainID,
  [ChainID.CORN_TESTNET]: 'corn_testnet' as ChainID,

  [ChainID.GOAT]: 'goat_mainnet' as ChainID,
  [ChainID.GOAT_TESTNET]: 'goat_testnet' as ChainID,

  [ChainID.SWELL_SEPOLIA]: 'swell_sepolia' as ChainID,

  [ChainID.BOTANIX]: 'botanix_mainnet' as ChainID,
  [ChainID.BOTANIX_TESTNET]: 'botanix_testnet' as ChainID,

  [ChainID.REDBELLY]: 'redbelly_mainnet' as ChainID,

  [ChainID.ROBINHOOD]: 'robinhood_mainnet' as ChainID,

  [ChainID.SONIC]: 'sonic_mainnet' as ChainID,
  [ChainID.SONIC_BLAZE_TESTNET]: 'sonic_blaze_testnet' as ChainID,

  [ChainID.STATUS_TESTNET]: 'status_testnet' as ChainID,

  [ChainID.MATCHAIN]: 'matchain_mainnet' as ChainID,

  [ChainID.IOTA]: 'iota_mainnet' as ChainID,
  [ChainID.IOTA_EVM_TESTNET]: 'iota_evm_testnet' as ChainID,
  [ChainID.IOTA_TESTNET]: 'iota_testnet' as ChainID,

  [ChainID.SOMNIA]: 'somnia_mainnet' as ChainID,
  [ChainID.SOMNIA_TESTNET]: 'somnia_testnet' as ChainID,

  [ChainID.MOVEMENT_BARDOCK_REST]: 'movement_bardock' as ChainID,
  [ChainID.MOVEMENT_MAINNET_REST]: 'movement_mainnet' as ChainID,

  [ChainID.NEURA_TESTNET]: 'neura_testnet' as ChainID,

  [ChainID.WHITECHAIN_TESTNET]: 'whitechain_testnet' as ChainID,

  [ChainID.MULTICHAIN]: 'aapi_v2' as ChainID,

  [ChainID.XRP]: 'xrp_mainnet' as ChainID,

  [ChainID.ETHERLINK]: 'etherlink_mainnet' as ChainID,
  [ChainID.ETHERLINK_TESTNET]: 'etherlink_testnet' as ChainID,
  [ChainID.ETHERLINK_SHADOWNET_TESTNET]:
    'etherlink_shadownet_testnet' as ChainID,

  [ChainID.MONAD]: 'monad_mainnet' as ChainID,
  [ChainID.MONAD_TESTNET]: 'monad_testnet' as ChainID,

  [ChainID.MIDNIGHT]: 'midnight_mainnet' as ChainID,
  [ChainID.MIDNIGHT_TESTNET]: 'midnight_testnet' as ChainID,

  [ChainID.ZKVERIFY_MAINNET]: ZKVERIFY_MAINNET_PATH,
  [ChainID.ZKVERIFY_VOLTA_TESTNET]: ZKVERIFY_VOLTA_TESTNET_PATH,
  [ChainID.ZKVERIFY_VFLOW_PARACHAIN_MAINNET]:
    ZKVERIFY_VFLOW_PARACHAIN_MAINNET_PATH,
  [ChainID.ZKVERIFY_VFLOW_PARACHAIN_TESTNET]:
    ZKVERIFY_VFLOW_PARACHAIN_TESTNET_PATH,

  [ChainID.XPHERE]: 'xphere_mainnet' as ChainID,
  [ChainID.XPHERE_TESTNET]: 'xphere_testnet' as ChainID,

  [ChainID.ATLETA]: 'atleta_mainnet' as ChainID,

  [ChainID.TAIKO_HOODI]: 'taiko_hoodi_testnet' as ChainID,

  [ChainID.DOGEOS_TESTNET]: 'dogeos_testnet' as ChainID,

  [ChainID.POLKADOT_ASSET_HUB]: POLKADOT_ASSET_HUB_PATH,
  [ChainID.POLKADOT_RPC]: 'polkadot' as ChainID,

  [ChainID.UNICHAIN]: 'unichain_mainnet' as ChainID,
};

type Map = Record<Timeframe, StatsTimeframe>;
export const chainsTimeframeToStatsTimeframeMap: Map = {
  [Timeframe.Hour]: StatsTimeframe.Hour,
  [Timeframe.Day]: StatsTimeframe.Day,
  [Timeframe.Week]: StatsTimeframe.Week,
  [Timeframe.Month]: StatsTimeframe.Month,
};

export const PRODUCTS_PAGE_PATHNAME = '/web3-api/chains-list/:chainID/';

export const CHAINS_PAGE_WIDTH = 1120;

export const SHORT_TIMEFRAME_LIST = [Timeframe.Hour, Timeframe.Day];

export const FULL_TIMEFRAME_LIST = [
  ...SHORT_TIMEFRAME_LIST,
  Timeframe.Week,
  Timeframe.Month,
];

export const chainTypeMap: Record<ChainType, keyof GroupedEndpoints> = {
  [ChainType.Devnet]: 'devnet',
  [ChainType.Mainnet]: 'mainnet',
  [ChainType.Testnet]: 'testnet',
  [ChainType.Signet]: 'signet',
};

export const fallbackChain: Chain = {
  id: ChainID.UNDEFINED,
  isComingSoon: false,
  urls: [
    {
      rpc: 'fakeChainUrl',
      enterprise: 'fakeEnterpriseChainUrl',
      enterpriseWs: 'fakeEnterpriseWsUrl',
    },
  ],
  hasGRPCFeature: false,
  hasWSFeature: false,
  hasRESTFeature: false,
  hasRPCFeature: false,
  hasEnterpriseFeature: false,
  coinName: '',
  name: 'fakeChain',
  type: EBlockchainType.Mainnet,
  features: [EBlockchainFeature.RPC],
};

export enum Technology {
  CURL,
  GO,
  PYTHON,
  WEB3_JS,
}

export enum ConnectionType {
  HTTP,
  WSS,
}
